import React, { useEffect, useState } from 'react';

import { useAppDispatch } from 'src/store';
import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import TypeToConfirmDialog from 'src/app/admin/components/type-to-confirm-dialog/TypeToConfirmDialog';
import BlastRadiusList from 'src/app/admin/components/blast-radius/BlastRadiusList';
import { useDeleteProjectMutation, useProjectStatsQuery } from 'src/app/admin/state/api/adminGraphSlice';
import { describeDeleteFailure } from 'src/app/admin/state/api/describeDeleteFailure';
import { DeleteProjectResult } from 'src/app/admin/state/api/queries/projectAdminQuery';

type DeleteProjectDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: string;
    projectName: string;
};

type Removed = DeleteProjectResult['admin']['deleteProject'];

const count = (value: number) => value.toLocaleString('en-GB');

const DeleteProjectDialog = ({ open, onOpenChange, projectId, projectName }: DeleteProjectDialogProps) => {
    const { data: stats, isLoading, isError } = useProjectStatsQuery({ projectId }, { skip: !open });
    const dispatch = useAppDispatch();
    const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [removed, setRemoved] = useState<Removed | null>(null);

    useEffect(() => {
        if (!open) {
            setErrorMessage(null);
            setRemoved(null);
        }
    }, [open]);

    const handleConfirm = async () => {
        setErrorMessage(null);
        try {
            const result = await deleteProject({ projectId }).unwrap();
            setRemoved(result.admin.deleteProject);
        } catch (error) {
            const failure = describeDeleteFailure(error);

            // A rejected mutation invalidates nothing, so a project that has gone behind our back
            // would keep its row. Re-read the lists ourselves to match what we tell the operator.
            if (failure.alreadyGone) {
                dispatch(graphApiSlice.util.invalidateTags(['project', 'customer']));
            }

            setErrorMessage(failure.message);
        }
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && removed) {
            // Deferred to here rather than done by the mutation, so re-reading the lists cannot
            // unmount this dialog while the operator is still reading what went.
            dispatch(graphApiSlice.util.invalidateTags(['project', 'customer']));
        }
        onOpenChange(nextOpen);
    };

    return (
        <TypeToConfirmDialog
            open={open}
            onOpenChange={handleOpenChange}
            entityLabel='project'
            confirmationText={projectName}
            confirmationPrompt={`Type the project name "${projectName}" to confirm`}
            warning='This removes the project and everything below it, across every store. It cannot be undone.'
            blastRadius={
                <BlastRadiusList
                    figures={[
                        { label: 'Samples', value: stats?.sampleCount ?? 0 },
                        { label: 'Assets', value: stats?.assetCount ?? 0 },
                        { label: 'Charts', value: stats?.chartCount ?? 0 },
                    ]}
                    generatedAt={stats?.generatedAt}
                    isLoading={isLoading}
                    isError={isError}
                />
            }
            confirmLabel='Delete project'
            onConfirm={handleConfirm}
            isConfirming={isDeleting}
            errorMessage={errorMessage}
            // Given only when the cascade succeeded; anything else leaves the gate in place.
            receipt={
                removed ? (
                    <p>
                        <b>Project &ldquo;{projectName}&rdquo; deleted:</b>
                        <br />
                        {count(removed.sampleCount)} samples, {count(removed.assetCount)} assets and {count(removed.chartCount)} charts.
                    </p>
                ) : undefined
            }
        />
    );
};

export default DeleteProjectDialog;
